import { useState } from 'react'
import { ganadorAlTiempo, tiempoConcluido } from '@domino/core'
import { Cargando, Problema, Vacio } from '../componentes/Estados.js'
import { Icono } from '../componentes/Icono.js'
import { estadoDe, etiquetas, usePartidaDeMesa } from '../componentes/PlanillaDeMesa.js'
import { obtenerRepositorio } from '../datos/index.js'
import type { ManoEnVivo, PartidaEnVivo } from '../datos/index.js'
import {
  AvisoCorreccion,
  CabezaLibreta,
  Cuaderno,
  Lado,
  puntosDe,
  Salida,
  Teclado,
} from '../componentes/Libreta.js'
import { volver } from '../navegacion.js'

/**
 * La libreta de anotación de una mesa, a pantalla entera.
 *
 * Es la planilla pensada para usarse con una mano mientras se juega: el
 * marcador grande arriba, y abajo un teclado propio con los valores que más
 * salen, para no abrir nunca el teclado del teléfono (que tapa la mitad de la
 * pantalla justo cuando se quiere ver el marcador). Antes de tocar para quién
 * es la mano se ve cómo queda el marcador.
 *
 * Tiene pantalla propia y no va debajo de la mesa: se entra desde la tarjeta
 * de la mesa y se vuelve con el botón de arriba.
 *
 * Cualquier mano se puede corregir. «Corregir» abre el historial en vertical,
 * como la hoja de papel (una columna por bando), y la mano que se toca se
 * edita con el mismo teclado con el que se anota.
 */

export function Anotacion({ torneoId, mesaId }: { torneoId: string; mesaId: string }) {
  const { partida, error, cargando, recargar, alEscribir } = usePartidaDeMesa(torneoId, mesaId)
  const alVolver = () => volver({ nombre: 'torneo', id: torneoId, pestana: 'mesa' })

  return (
    <div className="pantalla libreta-pantalla">
      {/* Con la libreta abierta, volver va en su primera fila. */}
      {(!partida || partida.modo === 'desactivada') && (
        <button className="libreta-volver" onClick={alVolver}>
          <Icono nombre="atras" tamano={20} />
          Volver a mi mesa
        </button>
      )}

      {error && !partida && <Problema mensaje={error} alReintentar={recargar} />}
      {!partida && !error && cargando && <Cargando que="Abriendo la libreta de la mesa…" />}
      {partida &&
        (partida.modo === 'desactivada' ? (
          <Vacio titulo="Este torneo no usa la libreta">
            <p>El resultado de la mesa lo carga el organizador al terminar la partida.</p>
          </Vacio>
        ) : (
          <Libreta
            partida={partida}
            torneoId={torneoId}
            alEscribir={alEscribir}
            alRefrescar={recargar}
            refrescando={cargando}
            alVolver={alVolver}
          />
        ))}
    </div>
  )
}

function Libreta({
  partida,
  torneoId,
  alEscribir,
  alRefrescar,
  refrescando,
  alVolver,
}: {
  partida: PartidaEnVivo
  torneoId: string
  alEscribir: (partida: PartidaEnVivo) => void
  alRefrescar: () => void
  refrescando: boolean
  alVolver: () => void
}) {
  const { miBando, nombre, quienes } = etiquetas(partida.jugadores)
  /* El bando propio va a la izquierda: es el que se busca primero. */
  const orden: [1 | 2, 1 | 2] = miBando === 2 ? [2, 1] : [1, 2]

  const [cifras, setCifras] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [aviso, setAviso] = useState<string | null>(null)
  /* Corrigiendo: el historial a la vista, y la mano tocada (si hay) en el teclado. */
  const [corrigiendo, setCorrigiendo] = useState(false)
  const [mano, setMano] = useState<ManoEnVivo | null>(null)

  const valor = Number(cifras)
  const listo = cifras !== '' && valor >= 1
  const alTiempo = tiempoConcluido(partida.venceEn)
  const empatados = ganadorAlTiempo(partida) === null
  const puede = partida.puedoCargar && !partida.concluida
  /* Con el tiempo concluido no se corrige: cambiaría un marcador que el reloj
     ya cantó. Con la partida terminada sí, por si la mano que la cerró se
     anotó mal. */
  const puedeCorregir = partida.puedoCargar && !alTiempo && partida.manos.length > 0
  const numero = partida.manos.length + 1
  const ultima = partida.manos[partida.manos.length - 1]?.numero ?? null

  function elegir(m: ManoEnVivo) {
    setMano(m)
    setCifras(String(m.puntos))
    setAviso(null)
  }

  function empezarCorreccion() {
    setCorrigiendo(true)
    setMano(null)
    setCifras('')
    setAviso(null)
  }

  function terminarCorreccion() {
    setCorrigiendo(false)
    setMano(null)
    setCifras('')
    setAviso(null)
  }

  /** Manda una escritura y dice si salió bien. */
  async function escribir(accion: () => Promise<PartidaEnVivo>): Promise<boolean> {
    setEnviando(true)
    setAviso(null)
    try {
      alEscribir(await accion())
      return true
    } catch (e) {
      setAviso(e instanceof Error ? e.message : String(e))
      /* Casi siempre es que otro de la mesa anotó primero: traer la partida
         nueva es lo que le muestra qué pasó. */
      alRefrescar()
      return false
    } finally {
      setEnviando(false)
    }
  }

  async function anotar(ganador: 1 | 2) {
    if (!listo) return
    if (alTiempo) {
      const queda = { bando1: partida.bando1, bando2: partida.bando2 }
      if (ganador === 1) queda.bando1 += valor
      else queda.bando2 += valor
      if (ganadorAlTiempo(queda) !== null) {
        const marcador = orden.map((b) => `${nombre[b]} ${puntosDe(b, queda)}`).join(', ')
        if (!window.confirm(`Es la última mano: la mesa se cierra con ${marcador}. ¿Anotarla?`)) {
          return
        }
      }
    }
    const repo = await obtenerRepositorio()
    const bien = await escribir(() =>
      repo.cargarMano(torneoId, partida.mesaId, { numero, bandoGanador: ganador, puntos: valor }),
    )
    if (bien) setCifras('')
  }

  async function guardar(ganador: 1 | 2) {
    if (!listo || !mano) return
    const repo = await obtenerRepositorio()
    const bien = await escribir(() =>
      repo.corregirMano(torneoId, partida.mesaId, mano.numero, {
        bandoGanador: ganador,
        puntos: valor,
      }),
    )
    if (bien) {
      setMano(null)
      setCifras('')
    }
  }

  /* Solo la última se borra (las demás se corrigen): es la que se anotó de más. */
  async function borrar() {
    if (!mano) return
    if (!window.confirm(`¿Borrar la mano M${mano.numero}?`)) return
    const repo = await obtenerRepositorio()
    const bien = await escribir(() => repo.borrarMano(torneoId, partida.mesaId, mano.numero))
    if (bien) {
      setMano(null)
      setCifras('')
    }
  }

  async function anotarSalida(salida: number | null) {
    const repo = await obtenerRepositorio()
    await escribir(() => repo.anotarSalida(torneoId, partida.mesaId, { silla: salida }))
  }

  /* Las restas del organizador entran en la tira en el lugar donde pasaron:
     el marcador ya las descontó, y sin verlas el número no cierra. */
  const recientes = [
    ...partida.manos.map((m) => ({
      clave: `m${m.numero}`,
      lugar: m.numero,
      resta: false,
      bando: m.bandoGanador,
      texto: `M${m.numero}: +${m.puntos}`,
    })),
    ...partida.restas.map((r, i) => ({
      clave: `r${i}`,
      lugar: r.despuesDeMano + 0.5,
      resta: true,
      bando: r.bando,
      texto: `−${r.puntos} (${r.nombre.split(' ')[0]})`,
    })),
  ]
    .sort((a, b) => a.lugar - b.lugar)
    .reverse()

  return (
    <div className={`libreta ${corrigiendo ? 'corrigiendo' : ''}`}>
      <CabezaLibreta textoVolver="Mi mesa" alVolver={alVolver}>
        Ronda {partida.rondaNumero} · Mesa <b>{partida.mesaNumero}</b>
      </CabezaLibreta>

      <div className="libreta-marcador" aria-live="polite">
        {/* Arriba del marcador van los nombres y no «Nosotros» y «Ellos»: se
            mira de reojo, y el nombre dice de quién es el número sin pensar. */}
        <Lado partida={partida} bando={orden[0]} nombre={quienes[orden[0]]} />
        <div className="libreta-a">
          a<b>{partida.objetivoPuntos}</b>
        </div>
        <Lado partida={partida} bando={orden[1]} nombre={quienes[orden[1]]} derecha />
      </div>

      {corrigiendo ? (
        <>
          <AvisoCorreccion
            mano={mano}
            quienes={quienes}
            alSoltar={() => {
              setMano(null)
              setCifras('')
            }}
          />

          <Cuaderno
            partida={partida}
            orden={orden}
            quienes={quienes}
            elegida={mano?.numero ?? null}
            alElegir={elegir}
          />

          {mano ? (
            <Teclado
              titulo={`Corregir M${mano.numero}`}
              cifras={cifras}
              setCifras={setCifras}
              listo={listo}
              enviando={enviando}
              orden={orden}
              nombre={nombre}
              elegido={mano.bandoGanador}
              resultado={(b) => {
                /* Cómo queda el bando si la mano pasa a ser suya. */
                const sin = puntosDe(b, partida) - (mano.bandoGanador === b ? mano.puntos : 0)
                return `${puntosDe(b, partida)} → ${sin + valor}`
              }}
              alElegir={(b) => void guardar(b)}
            />
          ) : null}

          <div className="libreta-correccion-acciones">
            {mano && mano.numero === ultima && (
              <button className="libreta-borrar" onClick={() => void borrar()} disabled={enviando}>
                Borrar esta mano
              </button>
            )}
            <button className="boton principal" onClick={terminarCorreccion} disabled={enviando}>
              Listo
            </button>
          </div>
        </>
      ) : (
        <>
          {recientes.length > 0 ? (
            <div className="libreta-manos" aria-label="Manos anotadas, la última primero">
              {recientes.map((m) => (
                <span key={m.clave} className={`libreta-mano ${m.resta ? 'resta' : ''}`}>
                  <i className={m.bando === orden[0] ? 'nosotros' : 'ellos'} />
                  {m.texto}
                </span>
              ))}
            </div>
          ) : (
            <p className="libreta-vacia">
              {puede
                ? 'Todavía no se anotó ninguna mano. Cualquiera de la mesa puede anotar la primera.'
                : 'Todavía no se anotó ninguna mano.'}
            </p>
          )}

          {!partida.concluida && (
            <Salida
              sillas={partida.sillas}
              salida={partida.salida}
              numero={numero}
              orden={orden}
              puede={puede}
              enviando={enviando}
              alAnotar={(salida) => void anotarSalida(salida)}
            />
          )}

          {puede && alTiempo && (
            <p className="planilla-form-ultima" role="status">
              <strong>{empatados ? 'Mano de desempate' : 'Última mano'}</strong>
              {empatados
                ? 'Se acabó el tiempo y van empatados. Si con esta mano uno queda arriba, se cierra la mesa; si no, se juega otra.'
                : 'Se acabó el tiempo. Al anotar esta mano se cierra la mesa, salvo que queden empatados.'}
            </p>
          )}

          {puede && (
            <Teclado
              titulo={`Mano ${numero}`}
              cifras={cifras}
              setCifras={setCifras}
              listo={listo}
              enviando={enviando}
              orden={orden}
              nombre={nombre}
              resultado={(b) => `${puntosDe(b, partida)} → ${puntosDe(b, partida) + valor}`}
              alElegir={(b) => void anotar(b)}
            />
          )}

          {puedeCorregir && (
            <button className="libreta-corregir" onClick={empezarCorreccion} disabled={enviando}>
              Corregir
            </button>
          )}
        </>
      )}

      {aviso && (
        <p className="planilla-aviso" role="alert">
          {aviso}
        </p>
      )}

      <footer className="planilla-pie">
        <span>{estadoDe(partida, nombre)}</span>
        <button
          className="mesas-refrescar"
          onClick={alRefrescar}
          disabled={refrescando}
          title="Volver a pedir la libreta"
        >
          <Icono nombre="refrescar" tamano={15} />
          Actualizar
        </button>
      </footer>
    </div>
  )
}
